import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useIsAdmin from "../../../hooks/useIsAdmin";
import {
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setFilterCategoryId,
  setFilterSearchString,
  setSelectedWorkshop,
  setSort,
  setFilter,
  resetFilter,
} from "../../../state/workshopSlice";
import style from "./ListFilters.module.css";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import AddTaskIcon from "@mui/icons-material/AddTask";

const ListFilters = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const categories = useSelector((state) => state.workshops.categories);
  //filter values
  const { showCreatedByUser, search, categoryId, sortBy, sortDirection } =
    useSelector((state) => state.workshops.filter);

  const newWorkshopClicked = () => {
    dispatch(setSelectedWorkshop(null));
    navigate("/edit/new");
  };

  const createdByMeToggled = () => {
    dispatch(setFilter({ showCreatedByUser: !showCreatedByUser }));
  };

  const searchStringChanged = (event) => {
    dispatch(setFilter({ search: event.target.value }));
  };

  const categoryChanged = (event) => {
    dispatch(setFilter({ categoryId: event.target.value }));
  };

  const sortByChanged = (event) => {
    dispatch(setFilter({ sortBy: event.target.value }));
  };

  const sortDirectionChanged = () => {
    dispatch(
      setFilter({ sortDirection: sortDirection === "desc" ? "asc" : "desc" })
    );
  };

  const resetFilterClicked = () => {
    dispatch(resetFilter());
  };

  const sortByOptions = [
    { name: "Title", value: "title" },
    { name: "Start Date", value: "startDatetime" },
  ];

  return (
    <>
      {isAdmin && (
        //new workshop button is only visible to admin
        <>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={newWorkshopClicked}
            sx={{ marginBottom: "0.5rem" }}
          >
            Create Workshop
          </Button>
        </>
      )}

      {!isAdmin && (
        //this is displayed for presenters
        <Box
          sx={{
            width: "100%",
            backgroundColor: "primary.light",
            padding: ".5rem",
            marginBottom: "0.5rem",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            color: "white",
          }}
        >
          <RecordVoiceOverIcon fontSize="medium" />
          <Typography variant="h4">Presenter View</Typography>
        </Box>
      )}

      <Card
        className={style.filter}
        sx={{ bgcolor: "background.main", overflow: "visible" }}
      >
        <FormGroup>
          <Box className={style.filterSection}>
            <Grid2 container spacing={1}>
              <Grid2
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h5">Filter Workshops</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        color="warning"
                        checked={showCreatedByUser}
                        onChange={createdByMeToggled}
                      />
                    }
                    label="Created by me"
                  />
                </FormGroup>
              </Grid2>
              <Grid2 lg={6} md={12} xs={12}>
                <FormControl fullWidth>
                  <TextField
                    name="search"
                    labelid="search-label"
                    label="Search"
                    value={search}
                    onChange={searchStringChanged}
                    size="small"
                    sx={{ backgroundColor: "white" }}
                  />
                </FormControl>
              </Grid2>
              <Grid2 lg={6} md={12} xs={12}>
                <Box className={style.sort}>
                  <FormControl size="small">
                    <InputLabel id="sortby-label">Sort by</InputLabel>
                    <Select
                      labelid="sortby-label"
                      id="sortby"
                      value={sortBy}
                      label="Sort by"
                      onChange={sortByChanged}
                      sx={{ backgroundColor: "white" }}
                    >
                      {sortByOptions.map((sort) => (
                        <MenuItem key={sort.value} value={sort.value}>
                          {sort.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Tooltip title="Reverse sort order" placement="right">
                    <IconButton
                      onClick={sortDirectionChanged}
                      sx={{ marginLeft: "0.25rem" }}
                    >
                      <SwapVertIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid2>
              <Grid2 xs={12}>
                <Box sx={{ display: "flex" }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="category-filter-label">
                      Filter by category
                    </InputLabel>
                    <Select
                      labelid="category-filter-label"
                      id="category-filter"
                      value={categoryId}
                      label="Filter by category"
                      onChange={categoryChanged}
                      sx={{ backgroundColor: "white" }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.categoryId} value={cat.categoryId}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Reset filters" placement="right">
                    <IconButton onClick={resetFilterClicked}>
                      <HighlightOffOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid2>
            </Grid2>
          </Box>
        </FormGroup>
      </Card>
    </>
  );
};

export default ListFilters;
